import { isAxiosError } from 'axios';
import { logout, restoreSession } from '@erebus/http';
import type { SessionRestoreResult } from '@erebus/http';
import { readSavedState } from 'src/stores/index';
import { confirmLogout, isLogoutPending, markLogoutPending } from './logoutPending';

/** The parts of the auth store the cookie boot uses. */
interface AuthStoreLike {
  status: string;
  sessionActive: boolean;
  loadSessionState: () => Promise<unknown>;
  trackLogout: (logout: Promise<unknown>) => void;
}

/** How long boot waits to end a session a logout left behind. */
export const BOOT_LOGOUT_CAP_MS = 5000;

/** Keys bearer mode writes that cookie mode never does. */
const BEARER_KEYS = ['user-token', 'employee-id', 'client-id'];

/** The auth store's saved state (see stores/auth.js). */
const AUTH_STORE_KEY = 'auth';

/**
 * Removes what a bearer-mode login left in storage, so cookie mode starts
 * clean. Run once at boot, in cookie mode, before Pinia restores the stores.
 *
 * The saved auth store goes too when it still holds a bearer token. Its gate
 * flags (second factor, phone, password change) would otherwise steer the
 * router guards to pages cookie mode does not use. A store saved by cookie
 * mode has no token and is kept, so an offline reload has something to boot
 * from. One that cannot be read is dropped, as it cannot be trusted.
 *
 * Never `localStorage.clear()`: `locale` must survive.
 */
export function clearSavedBearerAuth(): void {
  for (const key of BEARER_KEYS) {
    localStorage.removeItem(key);
  }

  if (localStorage.getItem(AUTH_STORE_KEY) === null) return;
  const saved = readSavedState(AUTH_STORE_KEY);
  if (!saved || saved.token) {
    localStorage.removeItem(AUTH_STORE_KEY);
  }
}

/**
 * The session is gone, so drop what the last one saved, as a logout would. A
 * bearer reload with an expired token gets the same from its first 401.
 * Nothing to do when this browser was not signed in.
 */
function signOutLocally(
  authStore: AuthStoreLike,
  applicationStore: RestoreDeps['applicationStore'],
): void {
  if (authStore.status === 'success') {
    applicationStore.reset();
  }
}

/** Store actions the cookie boot runs after the session is back. */
interface RestoreDeps {
  authStore: AuthStoreLike;
  memberStore: { fetchUser: () => Promise<unknown> };
  applicationStore: { fetchFeatureFlags: () => Promise<void>; reset: () => void };
}

/**
 * Restores a cookie session on page load.
 *
 * Restores the Java token from the cookie, loads the login state from legacy
 * `/session`, then the user and the feature flags. That matches what a bearer
 * reload does today; the full `dataRefresh` stays a login-only step.
 *
 * Offline, the saved state stands in: a user who was signed in stays signed in
 * on what this browser already has. Back online, the first Java call gets a
 * 401 and `@erebus/http` fetches a token with the cookie; if the cookie is gone
 * the next Go call's 401 logs out.
 *
 * `/session` answering 401 or 403 means legacy refused the session, so it is
 * ended on both sides. Any other failure — a 5xx, no answer — is treated like
 * offline: the saved state stands, and nothing is cleared.
 *
 * Never rejects, like the console's bootstrap: an error here would stop the app
 * mounting.
 */
export async function restoreCookieSession({
  authStore,
  memberStore,
  applicationStore,
}: RestoreDeps): Promise<SessionRestoreResult> {
  // A logout that never reached Go: end that session first, and never
  // restore it. If Go still can't be reached, boot signed out and try again
  // next time.
  if (isLogoutPending()) {
    // Capped: on a slow network the page would otherwise stay blank. If it
    // doesn't settle in time the marker stays, and the next boot tries again.
    // A login waits for the real request, which runs on past the cap.
    const ending = confirmLogout(logout);
    authStore.trackLogout(ending);
    await Promise.race([ending, new Promise((done) => setTimeout(done, BOOT_LOGOUT_CAP_MS))]);
    authStore.sessionActive = false;
    return 'no-session';
  }

  const result = await restoreSession();

  // Offline, a user who was signed in stays signed in on the saved state.
  const bootFromSavedState = (): 'offline' => {
    authStore.sessionActive = authStore.status === 'success';
    return 'offline';
  };

  if (result === 'offline') {
    return bootFromSavedState();
  }

  if (result === 'no-session') {
    signOutLocally(authStore, applicationStore);
    return result;
  }

  try {
    await authStore.loadSessionState();
  } catch (err) {
    // A server error or no answer is like offline. Anything else — a refusal,
    // or a body the store cannot use — ends the session on both sides.
    const status = isAxiosError(err) ? err.response?.status : undefined;
    if (isAxiosError(err) && status !== 401 && status !== 403) {
      return bootFromSavedState();
    }
    const ended = await confirmLogout(logout);
    signOutLocally(authStore, applicationStore);
    // After the sign-out, which clears storage.
    if (!ended) {
      console.warn('Could not end the session on the server');
      markLogoutPending();
    }
    return 'no-session';
  }

  try {
    await memberStore.fetchUser();
  } catch {
    // The router guards still work without the user record.
  }
  await applicationStore.fetchFeatureFlags();

  return result;
}
